import { SelectWithSpinAndCustomMaxTag } from "@src/components/select/SelectWithCustomMaxTag";
import { FilterFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { Checkbox, Form, Input } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import React from "react";
import { InputSkeletonLayout } from "../../layout/input-skeleton-layout";
import { useLoadAbExploration } from "@src/hooks/ab-testing/use-ab-exploration-get-result";
import { useDebounce } from "@src/hooks/use-debounce";
import { config } from "@src/config";
import { FilterOperator, SupportedModelAlias } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { useTracking } from "react-tracking";
import { trackEventPayload } from "@src/util/track-event-payload";

export const VariantFilter: React.FC<{ form: FormInstance }> = ({ form }) => {
  const { trackEvent } = useTracking();
  const abaIdWatch = Form.useWatch(FilterFormFieldsEnum.ABA_ID, form);
  const productCodeWatch = Form.useWatch(FilterFormFieldsEnum.PRODUCT_CODE, form);
  const abaIdDebounce = useDebounce(abaIdWatch, config.DEBOUNCE_TIME.SHORT_DELAY);
  const isHasAbaId = abaIdDebounce && (typeof abaIdDebounce === "string" || typeof abaIdDebounce === "number");
  const isReady = productCodeWatch && isHasAbaId;

  const { result, isLoading } = useLoadAbExploration({
    productCode: productCodeWatch,
    modelAlias: SupportedModelAlias.TICKET_INFO,
    query: {
      dimensions: ["exp_group", "exp_group_alias"],
      filters: [
        {
          member: "aba_id",
          operator: FilterOperator.EQUALS,
          values: abaIdDebounce ? [abaIdDebounce.toString()] : [],
        },
      ].filter((f) => f.values && f.values.length > 0, []),
      limit: 10000,
      order: [["exp_group", "asc"]],
    },
    isSkip: !isReady,
  });

  const options = React.useMemo(() => {
    return (
      result?.map((item: any) => {
        return {
          label: <span className="truncate max-w-[380px]">{item["exp_group_alias"] ?? item["exp_group"] ?? ""}</span>,
          value: item["exp_group"],
          origin: item,
        };
      }) ?? []
    );
  }, [result]);

  React.useEffect(() => {
    form.setFieldsValue({
      [FilterFormFieldsEnum.VARIANTS_OPTIONS]: options?.map((item: any) => ({
        label: item.origin?.["exp_group_alias"] ?? item.origin?.["exp_group"],
        value: item.origin?.["exp_group"],
      })),
    });
    if (!form.getFieldValue(FilterFormFieldsEnum.IS_ABA_ID_CHANGED)) return;
    const newVariants = options?.map((item: any) => item.value);
    form.setFieldsValue({
      [FilterFormFieldsEnum.VARIANTS]: newVariants,
      [FilterFormFieldsEnum.EXTRA_VARIANTS]: newVariants,
    });
    trackEvent(trackEventPayload.generalInfoFieldUpdated({ name: "variants", value: newVariants }, undefined, true));
  }, [options]);

  React.useEffect(() => {
    form.setFieldValue(FilterFormFieldsEnum.IS_EXP_LOADING, isLoading);
  }, [form, isLoading]);

  return (
    <InputSkeletonLayout width={400} isLoading={isLoading} label="Variants">
      <Form.Item label="Variants" name={FilterFormFieldsEnum.VARIANTS}>
        <SelectWithSpinAndCustomMaxTag
          disabled={!isReady}
          mode="multiple"
          allowClear
          placeholder="Select Variant"
          options={isLoading || !isHasAbaId ? [] : options}
          style={{
            width: 400,
          }}
          onChange={(value) => {
            trackEvent(trackEventPayload.generalInfoFieldUpdated({ name: "variants", value: value }));
          }}
        />
      </Form.Item>
      <Form.Item name={FilterFormFieldsEnum.VARIANTS_OPTIONS} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={FilterFormFieldsEnum.IS_EXP_LOADING} valuePropName="checked" hidden>
        <Checkbox />
      </Form.Item>
    </InputSkeletonLayout>
  );
};
